// The progress visual on the tracking page.
//
// Seven wedges sit over the mark. Each finished stage uncovers one wedge.
// Warren is supplying the seal artwork. When it arrives, change SEAL_SRC below
// (or swap the img for the seal's own svg) and nothing else needs touching.

import logo from "@/assets/wealthy-steward-logo.png";

const SEAL_SRC = logo;

const WEDGE_COUNT = 7;

function wedgePath(index: number, total: number): string {
  const cx = 50;
  const cy = 50;
  const r = 52;
  const start = (index / total) * Math.PI * 2 - Math.PI / 2;
  const end = ((index + 1) / total) * Math.PI * 2 - Math.PI / 2;
  const x1 = cx + r * Math.cos(start);
  const y1 = cy + r * Math.sin(start);
  const x2 = cx + r * Math.cos(end);
  const y2 = cy + r * Math.sin(end);
  return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2} Z`;
}

export default function SealProgress({
  completed,
  label,
}: {
  completed: number;
  label: string;
}) {
  const done = Math.max(0, Math.min(WEDGE_COUNT, completed));
  const maskId = "seal-reveal-mask";

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative h-40 w-40 sm:h-48 sm:w-48">
        <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full" aria-hidden="true">
          <defs>
            <mask id={maskId}>
              <rect x="0" y="0" width="100" height="100" fill="black" />
              {Array.from({ length: WEDGE_COUNT }).map((_, i) =>
                i < done ? (
                  <path key={i} d={wedgePath(i, WEDGE_COUNT)} fill="white" />
                ) : null,
              )}
            </mask>
          </defs>
        </svg>

        <img
          src={SEAL_SRC}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-contain opacity-15 grayscale"
        />

        <img
          src={SEAL_SRC}
          alt={`Progress mark for your association, ${done} of ${WEDGE_COUNT} steps finished`}
          className="absolute inset-0 h-full w-full object-contain transition-opacity duration-500"
          style={{ mask: `url(#${maskId})`, WebkitMask: `url(#${maskId})` } as React.CSSProperties}
        />

        <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full">
          {Array.from({ length: WEDGE_COUNT }).map((_, i) => (
            <path
              key={i}
              d={wedgePath(i, WEDGE_COUNT)}
              fill="none"
              stroke="currentColor"
              strokeWidth="0.4"
              className={i < done ? "text-primary/40" : "text-muted-foreground/15"}
            />
          ))}
        </svg>
      </div>
      <p className="text-center text-sm font-medium text-muted-foreground">{label}</p>
    </div>
  );
}
